// src/views/TradingBots/pnl.jsx
import React, { useState } from "react";

const MORALIS_API_KEY =
  (Math.random() < 0.5
    ? process.env.REACT_APP_MORALIS_API_KEY
    : process.env.REACT_APP_MORALIS_API_KEY_2) ||
  process.env.REACT_APP_MORALIS_API_KEY;

const DEFAULT_ADDRESS = "0x7081d3fb56F71868ad55780582Bc37bBC536e6D6";
const CHAIN = "cronos";

// original static defaults (you can change if you want)
const DEFAULT_FROM = "2025-11-13T00:00:00.000Z";
const DEFAULT_TO = "2025-11-13T23:59:35.000Z";

// Approx gas in CRO per swap, if you want to deduct it globally
const GAS_PER_SWAP = 0.07125;

const CRO_SYMBOLS = new Set(["CRO", "WCRO"]);
const NATIVE_TOKEN_ADDRESS = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";

// ----------------- helpers -----------------

function fmt(num, decimals = 4) {
  if (num == null || !isFinite(num)) return "—";
  return Number(num).toFixed(decimals);
}

function isoTrimMillis(d) {
  // returns YYYY-MM-DDTHH:mm:ss.000Z
  return d.toISOString().replace(/\.\d{3}Z$/, ".000Z");
}

async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      accept: "application/json",
      "X-API-Key": MORALIS_API_KEY,
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("❌ HTTP error", res.status, url, text);
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json();
}

async function getSwaps(address, fromIso, toIso) {
  const url = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/swaps?chain=${CHAIN}&fromDate=${encodeURIComponent(
    fromIso
  )}&toDate=${encodeURIComponent(toIso)}&order=DESC&limit=100`;
  const data = await fetchJSON(url);
  const result = Array.isArray(data.result) ? data.result : [];
  if (!Array.isArray(data.result)) {
    console.warn("⚠ swaps.result is not an array, raw:", data);
  }
  return result;
}

async function getTokens(address) {
  const url = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/tokens?chain=${CHAIN}&exclude_spam=true&limit=25&exclude_native=false&max_token_inactivity=30`;
  const data = await fetchJSON(url);
  const result = Array.isArray(data.result) ? data.result : [];
  if (!Array.isArray(data.result)) {
    console.warn("⚠ tokens.result is not an array, raw:", data);
  }
  return result;
}

/**
 * Build cost basis map keyed by token_address (lowercase).
 * Uses *actual CRO amounts* from swaps, not prices.
 */
function buildTokenStats(swaps) {
  const stats = {};
  const issues = [];

  if (!Array.isArray(swaps)) {
    issues.push("Swaps payload is not an array.");
    console.warn("⚠ swaps not array:", swaps);
    return { stats, issues };
  }

  for (const s of swaps) {
    if (!s || !s.sold || !s.bought) {
      issues.push(
        `Swap missing sold/bought for tx ${s?.transactionHash || "unknown"}`
      );
      console.warn("⚠ swap missing legs:", s);
      continue;
    }

    const sold = s.sold;
    const bought = s.bought;

    const soldSym = sold.symbol;
    const boughtSym = bought.symbol;

    if (!soldSym || !boughtSym) {
      issues.push(
        `Swap missing symbol info on tx ${s.transactionHash || "unknown"}`
      );
      console.warn("⚠ missing symbols:", s);
      continue;
    }

    const soldIsCro = CRO_SYMBOLS.has(soldSym);
    const boughtIsCro = CRO_SYMBOLS.has(boughtSym);

    // BUY: CRO -> TOKEN
    if (soldIsCro && !boughtIsCro) {
      const addr = (bought.address || "").toLowerCase();
      if (!addr) {
        issues.push(
          `No token address on bought leg for tx ${
            s.transactionHash || "unknown"
          }`
        );
        console.warn("⚠ missing bought.address:", s);
        continue;
      }

      const entry =
        stats[addr] ||
        (stats[addr] = {
          tokenAddress: addr,
          symbol: boughtSym,
          croIn: 0,
          croOut: 0,
          qtyIn: 0,
          qtyOut: 0,
          trades: 0,
        });

      const croAmt = Math.abs(Number(sold.amount) || 0);
      const qty = Math.abs(Number(bought.amount) || 0);

      entry.croIn += croAmt;
      entry.qtyIn += qty;
      entry.trades += 1;
      continue;
    }

    // SELL: TOKEN -> CRO
    if (!soldIsCro && boughtIsCro) {
      const addr = (sold.address || "").toLowerCase();
      if (!addr) {
        issues.push(
          `No token address on sold leg for tx ${
            s.transactionHash || "unknown"
          }`
        );
        console.warn("⚠ missing sold.address:", s);
        continue;
      }

      const entry =
        stats[addr] ||
        (stats[addr] = {
          tokenAddress: addr,
          symbol: soldSym,
          croIn: 0,
          croOut: 0,
          qtyIn: 0,
          qtyOut: 0,
          trades: 0,
        });

      const croAmt = Math.abs(Number(bought.amount) || 0);
      const qty = Math.abs(Number(sold.amount) || 0);

      entry.croOut += croAmt;
      entry.qtyOut += qty;
      entry.trades += 1;
      continue;
    }

    // Non-CRO pairs or something exotic – skip but log.
    issues.push(
      `Skipped non-CRO swap ${s.transactionHash || "unknown"}: ${soldSym} → ${boughtSym}`
    );
  }

  return { stats, issues };
}

/**
 * Compute PnL per token and aggregate, everything denominated in CRO.
 */
async function computePnL({ address, fromIso, toIso }) {
  const [swaps, tokens] = await Promise.all([
    getSwaps(address, fromIso, toIso),
    getTokens(address),
  ]);

  const { stats: basisMap, issues: basisIssues } = buildTokenStats(swaps);
  const issues = [...basisIssues];

  // Find CRO token to get USD price → CRO FX
  const croToken =
    tokens.find(
      (t) =>
        t.native_token ||
        CRO_SYMBOLS.has(t.symbol) ||
        (t.token_address || "").toLowerCase() ===
          NATIVE_TOKEN_ADDRESS.toLowerCase()
    ) || null;

  let croUsd = null;
  if (croToken && croToken.usd_price != null) {
    croUsd = Number(croToken.usd_price) || null;
  } else {
    issues.push("Could not find CRO usd_price from tokens list.");
    console.warn("⚠ CRO price missing in tokens:", tokens);
  }

  const perToken = [];
  let totalCurrentValueCro = 0;
  let totalInvestedCro = 0;
  let totalRealizedFlowCro = 0;

  for (const tk of tokens) {
    const addr = (tk.token_address || "").toLowerCase();
    const sym = tk.symbol || "UNKNOWN";
    const name = tk.name || "";
    const isNative =
      tk.native_token ||
      CRO_SYMBOLS.has(sym) ||
      addr === NATIVE_TOKEN_ADDRESS.toLowerCase();

    const balance = Number(tk.balance_formatted || "0");
    const usdPrice = Number(tk.usd_price || "0");
    const usdValue = Number(tk.usd_value || "0");

    let currentValueCro = 0;

    if (isNative) {
      // CRO itself – 1 CRO = 1 CRO
      currentValueCro = balance;
    } else if (croUsd && usdPrice) {
      const priceCro = usdPrice / croUsd;
      currentValueCro = balance * priceCro;
    } else if (croUsd && usdValue) {
      currentValueCro = usdValue / croUsd;
      issues.push(
        `Used fallback USD→CRO valuation for ${sym} due to missing usd_price.`
      );
    } else {
      issues.push(`Missing price data for ${sym}; current value set to 0.`);
      console.warn("⚠ missing price for", sym, tk);
      currentValueCro = 0;
    }

    const basis = basisMap[addr];

    if (isNative) {
      perToken.push({
        tokenAddress: addr,
        symbol: sym,
        name,
        balance,
        currentValueCro,
        totalInvestedCro: 0,
        realizedFlowCro: 0,
        pnlCro: 0,
        pnlPct: null,
        avgEntryCro: null,
        trades: basis?.trades || 0,
        netQty: balance,
        qtyIn: basis?.qtyIn || 0,
        qtyOut: basis?.qtyOut || 0,
        isNative: true,
      });
      totalCurrentValueCro += currentValueCro;
      continue;
    }

    if (!basis) {
      perToken.push({
        tokenAddress: addr,
        symbol: sym,
        name,
        balance,
        currentValueCro,
        totalInvestedCro: 0,
        realizedFlowCro: 0,
        pnlCro: currentValueCro,
        pnlPct: null,
        avgEntryCro: null,
        trades: 0,
        netQty: balance,
        qtyIn: 0,
        qtyOut: 0,
        isNative: false,
      });
      totalCurrentValueCro += currentValueCro;
      continue;
    }

    const croIn = basis.croIn;
    const croOut = basis.croOut;
    const qtyIn = basis.qtyIn;
    const qtyOut = basis.qtyOut;

    const netQtyFromTrades = qtyIn - qtyOut;
    const netQty = balance;

    const diff = Math.abs(netQty - netQtyFromTrades);
    if (diff > Math.max(0.001 * Math.abs(netQtyFromTrades), 0.001)) {
      issues.push(
        `Balance mismatch for ${sym}: wallet=${fmt(
          netQty
        )}, trades=${fmt(netQtyFromTrades)}`
      );
      console.warn("⚠ balance vs trade mismatch for", sym, {
        netQtyFromTrades,
        balanceWallet: netQty,
      });
    }

    const netInvestedCro = croIn - croOut; // >0 means net CRO spent
    const realizedFlowCro = croOut - croIn; // CRO out - in
    const pnlCro = currentValueCro + realizedFlowCro;
    const pnlPct =
      netInvestedCro > 0 ? (pnlCro / netInvestedCro) * 100 : null;

    const avgEntryCro =
      netQty > 0 && netInvestedCro > 0 ? netInvestedCro / netQty : null;

    totalCurrentValueCro += currentValueCro;
    if (netInvestedCro > 0) totalInvestedCro += netInvestedCro;
    totalRealizedFlowCro += realizedFlowCro;

    perToken.push({
      tokenAddress: addr,
      symbol: sym,
      name,
      balance,
      currentValueCro,
      totalInvestedCro: netInvestedCro,
      realizedFlowCro,
      pnlCro,
      pnlPct,
      avgEntryCro,
      trades: basis.trades,
      netQty,
      qtyIn,
      qtyOut,
      isNative: false,
    });
  }

  const gasCostCro = swaps.length * GAS_PER_SWAP;
  const totalRealizedFlowWithGas = totalRealizedFlowCro - gasCostCro;
  const totalPnlCro = totalCurrentValueCro + totalRealizedFlowWithGas;

  return {
    summary: {
      croUsd,
      swapsCount: swaps.length,
      gasCostCro,
      totalCurrentValueCro,
      totalInvestedCro,
      totalRealizedFlowCro,
      totalRealizedFlowWithGas,
      totalPnlCro,
    },
    tokens: perToken.sort((a, b) => b.currentValueCro - a.currentValueCro),
    issues,
  };
}

// =========================
// React component
// =========================

export default function PnLCalculator({
  initialAddress,
  initialFrom,
  initialTo,
  onComputed,
}) {
  const [address, setAddress] = useState(initialAddress || DEFAULT_ADDRESS);
  const [fromIso, setFromIso] = useState(initialFrom || DEFAULT_FROM);
  const [toIso, setToIso] = useState(initialTo || DEFAULT_TO);
  const [preset, setPreset] = useState("custom");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ----- Preset range logic -----
  function applyPreset(value) {
    setPreset(value);

    if (value === "custom") return;

    const now = new Date();
    let from, to;

    switch (value) {
      case "24h": {
        to = now;
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      }
      case "today": {
        const y = now.getUTCFullYear();
        const m = now.getUTCMonth();
        const d = now.getUTCDate();
        from = new Date(Date.UTC(y, m, d, 0, 0, 0));
        to = new Date(Date.UTC(y, m, d, 23, 59, 59));
        break;
      }
      case "yesterday": {
        const y = now.getUTCFullYear();
        const m = now.getUTCMonth();
        const d = now.getUTCDate() - 1;
        from = new Date(Date.UTC(y, m, d, 0, 0, 0));
        to = new Date(Date.UTC(y, m, d, 23, 59, 59));
        break;
      }
      case "7d": {
        to = now;
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      }
      default:
        return;
    }

    setFromIso(isoTrimMillis(from));
    setToIso(isoTrimMillis(to));
  }

  async function run() {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const addr = (address || "").trim();
      if (!addr) throw new Error("Wallet address is required.");
      const out = await computePnL({ address: addr, fromIso, toIso });
      setData(out);

      if (onComputed) {
        try {
          onComputed({ data: out, address: addr, fromIso, toIso });
        } catch (cbErr) {
          console.error("⚠ onComputed callback errored:", cbErr);
        }
      }
    } catch (e) {
      console.error("❌ PnL computation failed:", e);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        padding: "1.5rem",
        background: "#050816",
        color: "#f9fafb",
        borderRadius: "1rem",
        border: "1px solid #1f2933",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      {/* Header + controls */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.2rem", marginBottom: 4 }}>
              📊 PnL Calculator (CRO-denominated)
            </h2>
            <div style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
              Chain: <span style={{ fontWeight: 600 }}>{CHAIN}</span>
            </div>
          </div>

          <button
            onClick={run}
            disabled={loading}
            style={{
              background:
                "linear-gradient(135deg, #4f46e5 0%, #ec4899 50%, #22c55e 100%)",
              border: "none",
              padding: "0.6rem 1.2rem",
              borderRadius: "999px",
              color: "white",
              cursor: loading ? "default" : "pointer",
              fontWeight: 600,
              fontSize: "0.9rem",
              opacity: loading ? 0.7 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {loading ? "Computing…" : "Compute PnL"}
          </button>
        </div>

        {/* Editable address + date range + presets */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr) minmax(160px, 1fr)",
            gap: "0.5rem 0.75rem",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>Wallet</div>
            <input
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
              }}
              spellCheck={false}
              style={{
                width: "100%",
                fontSize: "0.8rem",
                padding: "0.4rem 0.55rem",
                borderRadius: "0.5rem",
                border: "1px solid #1f2937",
                background: "#020617",
                color: "#e5e7eb",
                fontFamily: "monospace",
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              From (ISO UTC)
            </div>
            <input
              value={fromIso}
              onChange={(e) => {
                setFromIso(e.target.value);
                setPreset("custom");
              }}
              spellCheck={false}
              style={{
                width: "100%",
                fontSize: "0.8rem",
                padding: "0.4rem 0.55rem",
                borderRadius: "0.5rem",
                border: "1px solid #1f2937",
                background: "#020617",
                color: "#e5e7eb",
                fontFamily: "monospace",
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              To (ISO UTC)
            </div>
            <input
              value={toIso}
              onChange={(e) => {
                setToIso(e.target.value);
                setPreset("custom");
              }}
              spellCheck={false}
              style={{
                width: "100%",
                fontSize: "0.8rem",
                padding: "0.4rem 0.55rem",
                borderRadius: "0.5rem",
                border: "1px solid #1f2937",
                background: "#020617",
                color: "#e5e7eb",
                fontFamily: "monospace",
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              Range preset
            </div>
            <select
              value={preset}
              onChange={(e) => applyPreset(e.target.value)}
              style={{
                width: "100%",
                fontSize: "0.8rem",
                padding: "0.4rem 0.55rem",
                borderRadius: "999px",
                border: "1px solid #1f2937",
                background: "#020617",
                color: "#e5e7eb",
              }}
            >
              <option value="24h">Last 24 hours</option>
              <option value="today">Today (UTC)</option>
              <option value="yesterday">Yesterday (UTC)</option>
              <option value="7d">Last 7 days</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.75rem 1rem",
            borderRadius: "0.75rem",
            background: "#451a1a",
            border: "1px solid #b91c1c",
            fontSize: "0.85rem",
          }}
        >
          ❌ Error: {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                padding: "0.9rem 1rem",
                borderRadius: "0.9rem",
                background:
                  "radial-gradient(circle at 0% 0%, #1f2937 0, #020617 55%)",
                border: "1px solid #111827",
              }}
            >
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  marginBottom: 4,
                }}
              >
                Total PnL (after gas)
              </div>
              <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                {fmt(data.summary.totalPnlCro, 4)} CRO
              </div>
              <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                Gas cost: {fmt(data.summary.gasCostCro, 4)} CRO · Swaps:{" "}
                {data.summary.swapsCount}
              </div>
            </div>

            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                padding: "0.9rem 1rem",
                borderRadius: "0.9rem",
                background:
                  "radial-gradient(circle at 0% 0%, #111827 0, #020617 55%)",
                border: "1px solid #111827",
              }}
            >
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  marginBottom: 4,
                }}
              >
                Current portfolio value
              </div>
              <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                {fmt(data.summary.totalCurrentValueCro, 4)} CRO
              </div>
              <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                Total invested in tokens:{" "}
                {fmt(data.summary.totalInvestedCro, 4)} CRO
              </div>
            </div>

            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                padding: "0.9rem 1rem",
                borderRadius: "0.9rem",
                background:
                  "radial-gradient(circle at 0% 0%, #0f172a 0, #020617 55%)",
                border: "1px solid #111827",
              }}
            >
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  marginBottom: 4,
                }}
              >
                CRO price snapshot
              </div>
              <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                {data.summary.croUsd ? `$${fmt(data.summary.croUsd, 5)}` : "—"}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                Realized flow (CRO out − in):{" "}
                {fmt(data.summary.totalRealizedFlowWithGas, 4)} CRO (incl. gas)
              </div>
            </div>
          </div>

          {/* Per-token breakdown */}
          <div
            style={{
              fontSize: "0.9rem",
              marginBottom: "0.5rem",
              color: "#a5b4fc",
              fontWeight: 600,
            }}
          >
            ✳ Per-token breakdown (values in CRO)
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            {data.tokens.map((tk) => {
              const pnlColor =
                tk.pnlCro > 0
                  ? "#22c55e"
                  : tk.pnlCro < 0
                  ? "#f97316"
                  : "#e5e7eb";

              return (
                <div
                  key={tk.tokenAddress + tk.symbol}
                  style={{
                    borderRadius: "0.9rem",
                    border: "1px solid #111827",
                    background:
                      "radial-gradient(circle at 0% 0%, #020617 0, #020617 65%)",
                    padding: "0.85rem 1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "1rem",
                      marginBottom: "0.4rem",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.95rem",
                          fontWeight: 700,
                          color: "#f9fafb",
                        }}
                      >
                        {tk.symbol}{" "}
                        <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                          · {tk.name}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "0.78rem",
                          color: "#6b7280",
                          fontFamily: "monospace",
                        }}
                      >
                        {tk.isNative ? "(native CRO)" : tk.tokenAddress}
                      </div>
                    </div>

                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: pnlColor,
                      }}
                    >
                      PnL: {fmt(tk.pnlCro, 4)} CRO{" "}
                      {tk.pnlPct != null && (
                        <span style={{ fontSize: "0.8rem" }}>
                          ({fmt(tk.pnlPct, 2)}%)
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit,minmax(140px,1fr))",
                      gap: "0.4rem 1.4rem",
                      fontSize: "0.8rem",
                      color: "#e5e7eb",
                    }}
                  >
                    <div>
                      <div style={{ color: "#9ca3af" }}>Balance</div>
                      <div>{fmt(tk.balance, 4)}</div>
                    </div>
                    <div>
                      <div style={{ color: "#9ca3af" }}>
                        Current value (CRO)
                      </div>
                      <div>{fmt(tk.currentValueCro, 4)}</div>
                    </div>
                    <div>
                      <div style={{ color: "#9ca3af" }}>
                        Total invested (CRO)
                      </div>
                      <div>{fmt(tk.totalInvestedCro, 4)}</div>
                    </div>
                    <div>
                      <div style={{ color: "#9ca3af" }}>
                        Realized flow (CRO out − in)
                      </div>
                      <div
                        style={{
                          color:
                            tk.realizedFlowCro > 0
                              ? "#22c55e"
                              : tk.realizedFlowCro < 0
                              ? "#f97316"
                              : "#e5e7eb",
                        }}
                      >
                        {fmt(tk.realizedFlowCro, 4)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#9ca3af" }}>
                        Avg entry (CRO / token)
                      </div>
                      <div>{fmt(tk.avgEntryCro, 6)}</div>
                    </div>
                    <div>
                      <div style={{ color: "#9ca3af" }}>
                        Trades (net qty from trades)
                      </div>
                      <div>
                        {tk.trades}{" "}
                        <span style={{ color: "#9ca3af" }}>
                          (qty net: {fmt(tk.netQty, 4)})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Issues / debugging info */}
          {data.issues && data.issues.length > 0 && (
            <div
              style={{
                marginTop: "1.5rem",
                padding: "0.75rem 1rem",
                borderRadius: "0.85rem",
                background: "#020617",
                border: "1px dashed #4b5563",
                fontSize: "0.8rem",
                color: "#9ca3af",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: "0.3rem" }}>
                ⚠ Diagnostics
              </div>
              <ul style={{ margin: 0, paddingLeft: "1rem" }}>
                {data.issues.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
              <div style={{ marginTop: "0.3rem" }}>
                Full details are also logged in the browser console.
              </div>
            </div>
          )}
        </>
      )}

      {!data && !loading && !error && (
        <div
          style={{
            marginTop: "0.75rem",
            fontSize: "0.8rem",
            color: "#9ca3af",
          }}
        >
          Set the wallet / date range (or choose a preset) and click{" "}
          <strong>Compute PnL</strong> to fetch swaps/balances from Moralis and
          generate a full per-token PnL breakdown in CRO.
        </div>
      )}
    </div>
  );
}

// expose computePnL so other components (like SniperBot) can reuse logic if needed
export { computePnL };

